import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaBath,
  FaCubes,
  FaHome,
  FaMousePointer,
  FaPaintRoller,
  FaPlay,
  FaSwatchbook
} from 'react-icons/fa';
import SEO, { siteUrl } from '../components/SEO';

const videoSrc = '/bathroom-configurator-demo.mp4';
const thumbnailImage = 'https://img.youtube.com/vi/uGDhoFjksEo/maxresdefault.jpg';

const featureCards = [
  {
    icon: FaMousePointer,
    title: 'Real-Time Customization',
    description: 'Adjust bathroom layouts and design choices instantly through an interactive product experience.'
  },
  {
    icon: FaSwatchbook,
    title: 'Material & Finish Selection',
    description: 'Preview surfaces, finishes, and product details with a polished visual presentation.'
  },
  {
    icon: FaCubes,
    title: 'Interactive Visualization',
    description: 'Explore design elements in a responsive real-time environment built for clarity and confidence.'
  },
  {
    icon: FaPaintRoller,
    title: 'Smooth Product Presentation',
    description: 'Showcase premium bathroom options with a refined flow made for client-facing demonstrations.'
  }
];

const reasons = [
  'Turn static bathroom concepts into an interactive design experience',
  'Help clients compare layouts, finishes, and product choices faster',
  'Present premium interiors with real-time visual feedback'
];

const useCases = [
  'Bathroom product showrooms',
  'Interior design presentations',
  'Material and finish previews',
  'Client product visualization'
];

const BathroomConfigurator: React.FC = () => {
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  const configuratorJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Bathroom Configurator',
    applicationCategory: 'DesignApplication',
    url: `${siteUrl}/bathroom-configurator`,
    image: thumbnailImage,
    description: 'Bathroom Configurator is a real-time interactive interior product configurator for premium bathroom visualization and customization.',
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/InStock',
      url: `${siteUrl}/contact?service=Bathroom%20Configurator%20Demo%20Request`
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-black via-gray-900 to-black pt-24">
      <SEO
        title="Bathroom Configurator - Real-Time Interior Visualization"
        description="Bathroom Configurator is an interactive real-time bathroom design experience for layout previews, material selection, product visualization, and client presentations."
        path="/bathroom-configurator"
        image={thumbnailImage}
        keywords={[
          'bathroom configurator',
          'real-time configurator',
          'interior visualization',
          'product configurator',
          'Unreal Engine configurator'
        ]}
        jsonLd={configuratorJsonLd}
      />
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.01)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.01)_1px,transparent_1px)] bg-[size:72px_72px] pointer-events-none" />
      <div className="absolute left-[-8rem] top-24 h-72 w-72 rounded-full bg-red-500/20 blur-3xl pointer-events-none" />
      <div className="absolute right-[-6rem] top-[32rem] h-80 w-80 rounded-full bg-red-600/15 blur-3xl pointer-events-none" />
      <div className="absolute bottom-24 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-red-500/10 blur-3xl pointer-events-none" />

      <section className="relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24 relative z-10">
          <div className="grid gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
            <div className="animate-slide-up">
              <div className="inline-flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/10 px-5 py-2 backdrop-blur-sm">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
                </span>
                <span className="font-display text-sm font-semibold text-red-400">Real-Time Application</span>
              </div>

              <h1 className="mt-8 max-w-4xl font-display text-5xl font-bold leading-tight text-white sm:text-6xl lg:text-7xl">
                Bathroom Configurator
                <span className="mt-2 block bg-gradient-to-r from-red-500 via-red-300 to-red-500 bg-[length:200%_100%] bg-clip-text text-transparent animate-gradient">
                  Interactive Real-Time Bathroom Design Experience
                </span>
              </h1>

              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-gray-400 sm:text-xl">
                Explore and customize bathroom layouts, materials, and design elements in a real-time interactive
                configurator built for premium product visualization.
              </p>

              <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                <Link
                  to="/contact?service=Bathroom%20Configurator%20Demo%20Request"
                  className="group inline-flex items-center justify-center gap-3 rounded-full border border-red-400/40 bg-gradient-to-r from-red-500 via-red-500 to-red-600 px-8 py-4 font-semibold text-white shadow-[0_0_30px_rgba(239,68,68,0.28)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_45px_rgba(239,68,68,0.5)]"
                >
                  <span>Request a Demo</span>
                  <FaArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>

              <div className="mt-10 grid max-w-2xl grid-cols-2 gap-4 sm:grid-cols-4">
                {['Unreal Engine', 'Configurator', 'Interior', 'Real-Time'].map((item) => (
                  <div
                    key={item}
                    className="rounded-2xl border border-white/10 bg-white/5 px-4 py-4 text-center text-sm font-medium text-gray-300 backdrop-blur-sm"
                  >
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div className="relative min-h-[420px] animate-scale-in sm:min-h-[520px] lg:min-h-[640px]">
              <div className="absolute inset-0 rounded-[2.5rem] border border-white/10 bg-[radial-gradient(circle_at_50%_32%,rgba(255,85,85,0.25),transparent_24%),linear-gradient(160deg,rgba(18,18,21,0.94)_0%,rgba(7,7,9,0.96)_52%,rgba(1,1,2,0.98)_100%)] shadow-2xl shadow-black/30" />
              <div className="absolute inset-6 rounded-[2rem] border border-red-500/20 bg-black/30 p-3 backdrop-blur-sm sm:inset-8 sm:p-4">
                <div className="relative flex h-full items-center overflow-hidden rounded-[1.5rem] border border-white/10 bg-black">
                  {isVideoLoaded ? (
                    <video
                      src={videoSrc}
                      className="aspect-video w-full bg-black object-contain"
                      controls
                      autoPlay
                      playsInline
                      poster={thumbnailImage}
                      preload="auto"
                      onErrorCapture={(event) => {
                        event.stopPropagation();
                      }}
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => setIsVideoLoaded(true)}
                      className="group relative block aspect-video w-full overflow-hidden bg-black text-left"
                      aria-label="Play Bathroom Configurator demo video"
                    >
                      <img
                        src={thumbnailImage}
                        alt="Bathroom Configurator real-time design preview"
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-black/20" />
                      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-red-300/70 to-transparent" />
                      <span className="absolute left-1/2 top-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-red-200/40 bg-red-500/20 text-white shadow-2xl shadow-red-500/30 backdrop-blur-md transition-all duration-300 group-hover:scale-110 group-hover:bg-red-500/30">
                        <FaPlay className="ml-1 text-2xl" />
                      </span>
                      <span className="absolute bottom-8 left-1/2 flex -translate-x-1/2 items-center gap-3 whitespace-nowrap rounded-full border border-red-500/30 bg-black/65 px-5 py-3 text-sm font-semibold text-red-300 backdrop-blur-md">
                        <FaBath />
                        Play Bathroom Configurator Demo
                      </span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 relative z-10">
          <div className="grid gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
            <div>
              <div className="inline-flex items-center rounded-full border border-red-500/30 bg-red-500/10 px-5 py-2 backdrop-blur-sm">
                <span className="font-display text-sm font-semibold text-red-400">Why Bathroom Configurator</span>
              </div>
              <h2 className="mt-6 font-display text-4xl font-bold text-white sm:text-5xl">
                Design choices become easier to <span className="text-red-500">compare</span>
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-gray-400">
                Bathroom Configurator gives clients a premium way to explore interiors, product options, and material
                combinations before committing to a final direction.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              {reasons.map((reason, index) => (
                <div
                  key={reason}
                  className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/5 to-white/0 p-6 backdrop-blur-xl transition-all duration-500 hover:-translate-y-2 hover:border-red-500/40 hover:shadow-2xl hover:shadow-red-500/10"
                >
                  <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-red-500/30 bg-red-500/10 font-display font-bold text-red-400">
                    0{index + 1}
                  </div>
                  <p className="font-semibold leading-relaxed text-white">{reason}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 relative z-10">
          <div className="mb-12 text-center">
            <div className="inline-flex items-center rounded-full border border-red-500/30 bg-red-500/10 px-5 py-2 backdrop-blur-sm">
              <span className="font-display text-sm font-semibold text-red-400">Key Features</span>
            </div>
            <h2 className="mt-6 font-display text-4xl font-bold text-white sm:text-5xl">
              Built for premium <span className="text-red-500">product visualization</span>
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {featureCards.map((feature, index) => (
              <div
                key={feature.title}
                className="group relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-white/5 to-white/0 p-8 backdrop-blur-xl transition-all duration-500 hover:-translate-y-2 hover:border-red-500/40 hover:shadow-2xl hover:shadow-red-500/10 animate-scale-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-red-500/0 to-red-500/0 opacity-0 transition-opacity duration-500 group-hover:from-red-500/10 group-hover:to-transparent group-hover:opacity-100" />
                <feature.icon className="relative text-5xl text-red-500 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6" />
                <h3 className="relative mt-6 font-display text-2xl font-bold text-white">{feature.title}</h3>
                <p className="relative mt-3 leading-relaxed text-gray-400">{feature.description}</p>
                <div className="absolute right-0 top-0 h-20 w-20 rounded-bl-3xl bg-gradient-to-br from-red-500/20 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 relative z-10">
          <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/10 to-white/0 p-6 shadow-2xl shadow-black/20 backdrop-blur-xl sm:p-10 lg:p-12">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_20%,rgba(255,85,85,0.20),transparent_30%),radial-gradient(circle_at_75%_80%,rgba(239,68,68,0.15),transparent_34%)]" />
            <div className="relative grid gap-10 lg:grid-cols-2 lg:items-center">
              <div>
                <div className="inline-flex items-center rounded-full border border-red-500/30 bg-red-500/10 px-5 py-2 backdrop-blur-sm">
                  <span className="font-display text-sm font-semibold text-red-400">Interactive Experience</span>
                </div>
                <h2 className="mt-6 font-display text-4xl font-bold text-white sm:text-5xl">
                  Explore the bathroom before it is built
                </h2>
                <p className="mt-5 text-lg leading-relaxed text-gray-400">
                  Layout options, material previews, and interior details come together in a real-time configurator
                  that makes bathroom product presentation feel direct and memorable.
                </p>
              </div>

              <div className="relative min-h-[320px] rounded-[2rem] border border-red-500/20 bg-black/40 p-8">
                <div className="absolute inset-x-8 top-10 h-px bg-gradient-to-r from-transparent via-red-300/60 to-transparent" />
                <div className="absolute left-8 top-16 h-20 w-28 rounded-2xl border border-red-500/40 bg-red-500/10" />
                <div className="absolute right-8 top-16 h-20 w-28 rounded-2xl border border-red-500/40 bg-red-500/10" />
                <div className="absolute inset-x-[18%] bottom-12 h-32 rounded-t-[2rem] border border-red-500/35 bg-red-500/10" />
                <div className="absolute bottom-24 left-1/2 h-20 w-20 -translate-x-1/2 rounded-full border-[10px] border-red-500/70 bg-black/60" />
                <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-3 rounded-full border border-white/10 bg-white/5 px-5 py-3 text-sm font-semibold text-white backdrop-blur-md">
                  <FaHome className="text-red-400" />
                  Live Configuration
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 relative z-10">
          <div className="mb-12 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="inline-flex items-center rounded-full border border-red-500/30 bg-red-500/10 px-5 py-2 backdrop-blur-sm">
                <span className="font-display text-sm font-semibold text-red-400">Use Cases</span>
              </div>
              <h2 className="mt-6 font-display text-4xl font-bold text-white sm:text-5xl">
                Ready for sales and <span className="text-red-500">design reviews</span>
              </h2>
            </div>
            <p className="max-w-xl text-base leading-relaxed text-gray-400 sm:text-lg">
              A real-time configurator gives teams a richer way to present bathroom products, interior options, and
              premium design packages.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {useCases.map((item) => (
              <div
                key={item}
                className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/5 to-white/0 p-7 backdrop-blur-xl transition-all duration-500 hover:-translate-y-2 hover:border-red-500/40 hover:shadow-2xl hover:shadow-red-500/10"
              >
                <div className="mb-6 h-1.5 w-20 rounded-full bg-gradient-to-r from-red-500 to-red-300" />
                <h3 className="font-display text-2xl font-bold text-white">{item}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 pb-20 relative z-10">
          <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/10 to-white/0 px-6 py-12 text-center shadow-2xl shadow-black/20 backdrop-blur-xl sm:px-10 sm:py-16">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,85,85,0.22),_transparent_55%)]" />
            <div className="absolute -top-12 left-1/2 h-32 w-32 -translate-x-1/2 rounded-full bg-red-500/20 blur-3xl" />
            <div className="relative">
              <h2 className="font-display text-3xl font-bold text-white sm:text-4xl lg:text-5xl">
                See Bathroom Configurator in action
              </h2>
              <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-gray-400">
                Request a personalized demo to explore the real-time customization and premium bathroom design workflow.
              </p>
              <Link
                to="/contact?service=Bathroom%20Configurator%20Demo%20Request"
                className="group mt-8 inline-flex items-center gap-3 rounded-full border border-red-400/40 bg-gradient-to-r from-red-500 via-red-500 to-red-600 px-8 py-4 font-semibold text-white shadow-[0_0_30px_rgba(239,68,68,0.28)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_45px_rgba(239,68,68,0.5)]"
              >
                <span>Request a Demo</span>
                <FaArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BathroomConfigurator;
